import { useCallback } from "react";

import {
  DriveEntryErrorById,
  FileUploadEntry,
  OrchidDriveData,
  OrchidDriveDirectory,
  OrchidDriveEntryKeys,
  OrchidDriveUploadTask,
  UploadEntry,
} from "../types";

import OrchidDriveEntryKeysProvider, {
  EntriesNeedKeysFunc,
  OrchidDriveKeysByEntryId,
} from "./orchidDriveEntryKeys";
import { directoryEntryCreate } from "./directoryEntry";
import {
  FileUploadProgress,
  LocalUploadTaskStatus,
} from "./fileUploadProgress";
import { InvalidArgumentError } from "utils/error";
import { batchEntryOps } from "./batchEntryOps";
import { requestEncryptionKeys } from "./entry";
import { orchid_encrypt } from "utils/OrchidEncryption";
import {
  UpdateAvailableDriveEntriesFunc,
  useOrchidDriveData,
} from "./orchidDriveData";
import { reportProblem } from "utils/frontend_error_reporting";
import { genUniqueId } from "utils/fn";

type UploadFileFuncArgsGeneric = {
  storageDrive: StorageDrive;
  patientUserId?: string;
  files: FileUploadEntry[];
};

type UploadFileFuncArgs = {
  files: FileUploadEntry[];
};

type UploadFileFuncArgsWithPatient = {
  patientUserId: string;
  files: FileUploadEntry[];
};

export type UploadFileGenericFunc = ({
  storageDrive,
  patientUserId,
  files,
}: UploadFileFuncArgsGeneric) => Promise<OrchidDriveUploadTask | undefined>;

export type UploadFileMemberDriveFunc = ({
  files,
}: UploadFileFuncArgs) => Promise<OrchidDriveUploadTask | undefined>;

export type UploadFilePatientChartFunc = ({
  patientUserId,
  files,
}: UploadFileFuncArgsWithPatient) => Promise<OrchidDriveUploadTask | undefined>;

export type UploadFileNoteAttachmentFunc = ({
  patientUserId,
  files,
}: UploadFileFuncArgsWithPatient) => Promise<OrchidDriveUploadTask | undefined>;

type UploadFileContinueFuncArgs = {
  fileDataId: string;
  file: File;
};

export type UploadFileContinueFunc = ({
  fileDataId,
  file,
}: UploadFileContinueFuncArgs) => Promise<void>;

export type UploadTaskAbortFunc = (
  args: { uploadEntry: UploadEntry } | { uploadTaskId: string },
) => Promise<void>;

type DownloadFileFuncArgs = {
  fileList: Array<{
    fileDataId: string;
    pathAndFileName: string;
  }>;
  zipFilename: string;
};

export type DownloadFilesFunc = ({
  fileList,
  zipFilename,
}: DownloadFileFuncArgs) => Promise<string>;

type MoveEntryFuncArgs = {
  entryId: string;
  name?: string;
  parentDirectoryId?: string | null;
};

export type MoveEntryFunc = ({
  entryId,
  name,
  parentDirectoryId,
}: MoveEntryFuncArgs) => Promise<void>;

type DeleteEntryFuncArgs = {
  entryId: string;
};

export type DeleteEntryFunc = ({
  entryId,
}: DeleteEntryFuncArgs) => Promise<void>;

type CreateDirectoryFuncArgsGeneric = {
  name: string;
  parentDirectoryId: string | null | undefined;
  storageDrive: StorageDrive;
  patientUserId?: string;
};

type CreateDirectoryFuncArgs = {
  name: string;
  parentDirectoryId: string | null | undefined;
};

type CreateDirectoryFuncArgsWithPatient = {
  name: string;
  parentDirectoryId: string | null | undefined;
  patientUserId: string;
};

export type CreateDirectoryFunc = ({
  name,
  parentDirectoryId,
  storageDrive,
  patientUserId,
}: CreateDirectoryFuncArgsGeneric) => Promise<OrchidDriveDirectory>;

export type CreateDirectoryMemberDriveFunc = ({
  name,
  parentDirectoryId,
}: CreateDirectoryFuncArgs) => Promise<OrchidDriveDirectory>;

export type CreateDirectoryPatientChartFunc = ({
  name,
  parentDirectoryId,
  patientUserId,
}: CreateDirectoryFuncArgsWithPatient) => Promise<OrchidDriveDirectory>;

export type CreateDirectoryNoteAttachmentFunc = ({
  name,
  parentDirectoryId,
  patientUserId,
}: CreateDirectoryFuncArgsWithPatient) => Promise<OrchidDriveDirectory>;

export type BatchEntryOpsFunc = ({
  entryMoves,
  entryDeletes,
}: {
  entryMoves: {
    entryId: string;
    name?: string;
    parentDirectoryId?: string | null;
  }[];
  entryDeletes: { entryId: string }[];
}) => Promise<void>;

function downloadFile(url) {
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = url;

  document.body.appendChild(link);
  link.click();

  setTimeout(() => link.parentNode && link.parentNode.removeChild(link), 0);
}

export function OrchidDriveProvider(usedEntryIds: Set<string>): {
  entriesNeedKeys: EntriesNeedKeysFunc;
  orchidDriveEntryKeys: OrchidDriveKeysByEntryId;
  updateAvailableDriveEntries: UpdateAvailableDriveEntriesFunc;
  uploadTaskUpdates: Map<string, OrchidDriveUploadTask>;
  localUploadTaskStatus: LocalUploadTaskStatus[];

  uploadFile: UploadFileGenericFunc;
  uploadFileMemberDrive: UploadFileMemberDriveFunc;
  uploadFilePatientChart: UploadFilePatientChartFunc;
  uploadFileNoteAttachment: UploadFileNoteAttachmentFunc;
  uploadFileContinue: UploadFileContinueFunc;
  uploadTaskAbort: UploadTaskAbortFunc;
  downloadFiles: DownloadFilesFunc;
  moveEntry: MoveEntryFunc;
  deleteEntry: DeleteEntryFunc;
  createDirectory: CreateDirectoryFunc;
  createDirectoryMemberDrive: CreateDirectoryMemberDriveFunc;
  createDirectoryPatientChart: CreateDirectoryPatientChartFunc;
  createDirectoryNoteAttachment: CreateDirectoryNoteAttachmentFunc;
  batchEntryOps: BatchEntryOpsFunc;
  orchidDriveUpdates: OrchidDriveData;
  orchidDriveError?: string;
  orchidDriveErrorByEntryId: DriveEntryErrorById;
} {
  const {
    entriesNeedKeys,
    addEntryKeys,
    orchidDriveEntryKeys,
    orchidDriveEntryKeysError,
    orchidDriveEntryKeysErrorByEntryId,
  } = OrchidDriveEntryKeysProvider();

  // TODO: we need a way to free unused entries
  const {
    addPendingDriveEntries,
    updateAvailableDriveEntries,
    orchidDriveFiles,
  } = useOrchidDriveData(entriesNeedKeys, orchidDriveEntryKeys);

  const {
    uploadTaskUpdates,
    localUploadTaskStatus,

    driveUpload,
    driveUploadAbort,
  } = FileUploadProgress(
    updateAvailableDriveEntries,
    addPendingDriveEntries,
    addEntryKeys,
  );

  const uploadFileFunc: UploadFileGenericFunc = useCallback(
    ({ storageDrive, patientUserId, files }: UploadFileFuncArgsGeneric) => {
      if (["member-drive"].includes(storageDrive) && patientUserId)
        throw new InvalidArgumentError(
          "Files in orchid member drive cannot be tied to a patient",
        );
      if (
        ["patient-chart", "note-attachment"].includes(storageDrive) &&
        !patientUserId
      )
        throw new InvalidArgumentError(
          "Files in patient chart and note attachment have to be tied to a specific patient",
        );
      return driveUpload(storageDrive, patientUserId || null, files);
    },
    [driveUpload],
  );

  const uploadFileMemberDriveFunc: UploadFileMemberDriveFunc = useCallback(
    ({ files }: UploadFileFuncArgs) => {
      return driveUpload("member-drive", null, files);
    },
    [driveUpload],
  );

  const uploadFilePatientChartFunc: UploadFilePatientChartFunc = useCallback(
    ({ patientUserId, files }: UploadFileFuncArgsWithPatient) => {
      return driveUpload("patient-chart", patientUserId, files);
    },
    [driveUpload],
  );

  const uploadFileNoteAttachmentFunc: UploadFileNoteAttachmentFunc =
    useCallback(
      ({ patientUserId, files }: UploadFileFuncArgsWithPatient) => {
        return driveUpload("note-attachment", patientUserId, files);
      },
      [driveUpload],
    );

  const uploadFileContinueFunc: UploadFileContinueFunc = useCallback(
    async ({ fileDataId, file }) => {
      // TODO
    },
    [],
  );

  const uploadTaskAbortFunc = useCallback(
    (args) => driveUploadAbort(args.uploadEntry || args.uploadTaskId),
    [driveUploadAbort],
  );

  const downloadFilesFunc: DownloadFilesFunc = useCallback(
    async ({
      fileList,
      zipFilename,
    }: DownloadFileFuncArgs): Promise<string> => {
      return new Promise((resolve, reject) => {
        const currentBatchDownloadName = `bd_${genUniqueId(16)}`;
        const batchDownloadUrl = `/drive/download/batch/${currentBatchDownloadName}`;

        // make sure it's removed eventually
        const timeout = setTimeout(() => {
          reject("failed to receive batch download info");
          navigator.serviceWorker?.removeEventListener(
            "message",
            handleMessage,
          );
        }, 15000);

        function handleMessage(event: MessageEvent) {
          if (
            event.data.type ===
            "orchid_drive_download_batch_download_info_request"
          ) {
            const { batchDownloadName } = event.data;

            if (currentBatchDownloadName !== batchDownloadName) {
              // request is not for us
              return;
            }

            const port = event.ports[0];
            if (!port) {
              reportProblem({
                reporter_module: "orchidDrive",
                context: { func: "OrchidDriveProvider/downloadFilesFunc" },
                frontend_error: `no channel port was passed`,
              });
              return; // this is odd :(
            }

            // reply to service worker
            port.postMessage({
              type: "orchid_drive_download_batch_download_info_response",
              fileList,
              zipFilename,
            });

            clearTimeout(timeout);
            navigator.serviceWorker?.removeEventListener(
              "message",
              handleMessage,
            );

            // give back batch download id
            resolve(currentBatchDownloadName);
          }
        }

        navigator.serviceWorker?.addEventListener(
          "message",
          handleMessage,
          false,
        );

        downloadFile(batchDownloadUrl);
      });
    },
    [],
  );

  const batchEntryOpsFunc = useCallback<BatchEntryOpsFunc>(
    async ({ entryMoves, entryDeletes }) => {
      const entryIdsMissingKeys = entryMoves
        .filter((e) => !(e.entryId in orchidDriveEntryKeys))
        .map((e) => e.entryId);
      const requestedKeys: { [name: string]: OrchidDriveEntryKeys } =
        entryIdsMissingKeys.length === 0
          ? {}
          : await requestEncryptionKeys(entryIdsMissingKeys);
      addEntryKeys(requestedKeys);
      const entriesWithKeys = entryMoves.map((e) => {
        const keys =
          orchidDriveEntryKeys[e.entryId] || entryIdsMissingKeys[e.entryId];
        if (!keys) {
          reportProblem({
            reporter_module: "orchidDrive",
            context: { func: "batchEntryOpsFunc", entry_id: e.entryId },
            frontend_error: `cipher key not found, this should never happen`,
          });

          throw new Error("encryption key missing");
        }
        return { ...e, metaDataCipherKey: keys.metaDataCipherKey };
      });
      const entryMovesEncrypted = await Promise.all(
        entriesWithKeys.map(async (e) => {
          return {
            entryId: e.entryId,
            nameEncrypted:
              e.name !== undefined
                ? await orchid_encrypt(e.name, e.metaDataCipherKey)
                : undefined,
            parentDirectoryId: e.parentDirectoryId,
          };
        }),
      );
      const { fileEntryChanges, directoryEntryChanges } = await batchEntryOps(
        entryMovesEncrypted,
        entryDeletes,
      );
      addPendingDriveEntries(fileEntryChanges, directoryEntryChanges);
    },
    [orchidDriveEntryKeys, addPendingDriveEntries, addEntryKeys],
  );

  const moveEntryFunc: MoveEntryFunc = useCallback(
    async ({ entryId, name, parentDirectoryId }) => {
      await batchEntryOpsFunc({
        entryMoves: [
          {
            entryId,
            name,
            parentDirectoryId,
          },
        ],
        entryDeletes: [],
      });
    },
    [batchEntryOpsFunc],
  );

  const deleteEntryFunc: DeleteEntryFunc = useCallback(
    async ({ entryId }) => {
      await batchEntryOpsFunc({
        entryMoves: [],
        entryDeletes: [{ entryId }],
      });
    },
    [batchEntryOpsFunc],
  );

  const createDirectoryFunc: CreateDirectoryFunc = useCallback(
    async ({
      name,
      parentDirectoryId,
      storageDrive,
      patientUserId,
    }: CreateDirectoryFuncArgsGeneric) => {
      if (["member-drive"].includes(storageDrive) && patientUserId)
        throw new InvalidArgumentError(
          "Files in orchid member drive cannot be tied to a patient",
        );
      if (
        ["patient-chart", "note-attachment"].includes(storageDrive) &&
        !patientUserId
      )
        throw new InvalidArgumentError(
          "Files in patient chart and note attachment have to be tied to a specific patient",
        );
      const result = await directoryEntryCreate(
        name,
        parentDirectoryId || null,
        storageDrive,
        patientUserId || null,
        null,
        null,
      );
      addEntryKeys({ [result.cipherKeys.entryId]: result.cipherKeys });
      updateAvailableDriveEntries([], [result.directoryEntry]);
      return result.directoryEntry;
    },
    [updateAvailableDriveEntries, addEntryKeys],
  );

  const createDirectoryMemberDriveFunc: CreateDirectoryMemberDriveFunc =
    useCallback(
      async ({ name, parentDirectoryId }: CreateDirectoryFuncArgs) => {
        const result = await directoryEntryCreate(
          name,
          parentDirectoryId || null,
          "member-drive",
          null,
          null,
          null,
        );
        addEntryKeys({ [result.cipherKeys.entryId]: result.cipherKeys });
        updateAvailableDriveEntries([], [result.directoryEntry]);
        return result.directoryEntry;
      },
      [updateAvailableDriveEntries, addEntryKeys],
    );

  const createDirectoryPatientChartFunc: CreateDirectoryPatientChartFunc =
    useCallback(
      async ({
        name,
        parentDirectoryId,
        patientUserId,
      }: CreateDirectoryFuncArgsWithPatient) => {
        const result = await directoryEntryCreate(
          name,
          parentDirectoryId || null,
          "patient-chart",
          patientUserId,
          null,
          null,
        );
        addEntryKeys({ [result.cipherKeys.entryId]: result.cipherKeys });
        updateAvailableDriveEntries([], [result.directoryEntry]);
        return result.directoryEntry;
      },
      [updateAvailableDriveEntries, addEntryKeys],
    );

  const createDirectoryNoteAttachmentFunc: CreateDirectoryNoteAttachmentFunc =
    useCallback(
      async ({
        name,
        parentDirectoryId,
        patientUserId,
      }: CreateDirectoryFuncArgsWithPatient) => {
        const result = await directoryEntryCreate(
          name,
          parentDirectoryId || null,
          "note-attachment",
          patientUserId,
          null,
          null,
        );
        addEntryKeys({ [result.cipherKeys.entryId]: result.cipherKeys });
        updateAvailableDriveEntries([], [result.directoryEntry]);
        return result.directoryEntry;
      },
      [updateAvailableDriveEntries, addEntryKeys],
    );

  return {
    entriesNeedKeys,
    orchidDriveEntryKeys,
    updateAvailableDriveEntries,
    uploadTaskUpdates,
    localUploadTaskStatus,

    uploadFile: uploadFileFunc,
    uploadFileMemberDrive: uploadFileMemberDriveFunc,
    uploadFilePatientChart: uploadFilePatientChartFunc,
    uploadFileNoteAttachment: uploadFileNoteAttachmentFunc,
    uploadFileContinue: uploadFileContinueFunc,
    uploadTaskAbort: uploadTaskAbortFunc,
    downloadFiles: downloadFilesFunc,
    moveEntry: moveEntryFunc,
    deleteEntry: deleteEntryFunc,
    createDirectory: createDirectoryFunc,
    createDirectoryMemberDrive: createDirectoryMemberDriveFunc,
    createDirectoryPatientChart: createDirectoryPatientChartFunc,
    createDirectoryNoteAttachment: createDirectoryNoteAttachmentFunc,
    batchEntryOps: batchEntryOpsFunc,
    orchidDriveUpdates: orchidDriveFiles,
    orchidDriveError: orchidDriveEntryKeysError,
    orchidDriveErrorByEntryId: orchidDriveEntryKeysErrorByEntryId,
  };
}
